import { type TreeLevel, type TreeNode } from '~/utils/tree/treeTypes';

/** `aria-setsize` for a run whose full length is not known yet — more pages are still to come. */
export const TREE_UNKNOWN_SET_SIZE = -1;

/** Where a flattened row sits, in the terms `aria-level` / `aria-posinset` / `aria-setsize` want. */
export type TreeRowPlacement = {
  /** Nesting level, `0` at the roots; rows stay one flat list so navigation is index-based. */
  depth: number;
  hasChildren: boolean;
  isExpanded: boolean;
  /**
   * Carried through from the node — see `TreeNode`'s own `isMatch`. Always placed, `undefined` and
   * all: a row is never short of it, so nothing constructing a placement may leave it out.
   */
  isMatch: boolean | undefined;
  /** 1-based place among the row's siblings. */
  positionInLevel: number;
  /** `TREE_UNKNOWN_SET_SIZE` while the run the row sits in still has pages to load. */
  siblingCount: number;
};

export type FlattenTreeOptions<TValue, TRow> = {
  /**
   * Splits a level into runs numbered independently of one another: a node the callback names joins
   * that run, and one it does not stays in the level's main run. Numbering is per run because a
   * position is only meaningful inside the group the row is read in, so a level drawn as two groups
   * must be counted as two. Without it a level is one run.
   */
  groupKey?: (node: TreeNode<TValue>, depth: number) => string | undefined;
  /**
   * The roots are a page of a longer list: every run drawn from that response — the main run and
   * each `groupKey` run at the root — announces its number unknown rather than as loaded.
   */
  hasMoreRootNodes?: boolean;
  /** This node's own children are a page of a longer list, so that run's size is announced unknown. */
  hasMoreChildren?: (node: TreeNode<TValue>) => boolean;
  isExpanded: (node: TreeNode<TValue>) => boolean;
  toRow: (node: TreeNode<TValue>, placement: TreeRowPlacement) => TRow;
};

/**
 * One pass over one level, so `positionInLevel` / `siblingCount` are counted in a single place.
 * Paging follows the response a level was drawn from: roots via `hasMoreRootNodes`, and a child
 * level via `hasMoreChildren` on its parent.
 */
const flattenLevel = <TValue, TRow>(
  level: TreeLevel<TValue>,
  depth: number,
  options: FlattenTreeOptions<TValue, TRow>,
  hasMoreInLevel: boolean,
): TRow[] => {
  const { groupKey, isExpanded, toRow } = options;
  const nodes = [...level.values()];
  const runSizes = new Map<string | undefined, number>();

  for (const node of nodes) {
    const runKey = groupKey?.(node, depth);

    runSizes.set(runKey, (runSizes.get(runKey) ?? 0) + 1);
  }

  const runPositions = new Map<string | undefined, number>();

  return nodes.flatMap((node) => {
    const runKey = groupKey?.(node, depth);
    const hasChildren = node.hasChildren ?? node.children.size > 0;
    const isNodeExpanded = hasChildren && isExpanded(node);
    const positionInLevel = (runPositions.get(runKey) ?? 0) + 1;

    runPositions.set(runKey, positionInLevel);

    const row = toRow(node, {
      depth,
      hasChildren,
      isExpanded: isNodeExpanded,
      isMatch: node.isMatch,
      positionInLevel,
      // One response can feed several runs of a level, and a run is paging when the response behind
      // it has pages left — so every run here is, not the main one alone.
      siblingCount: hasMoreInLevel ? TREE_UNKNOWN_SET_SIZE : (runSizes.get(runKey) ?? 0),
    });

    return isNodeExpanded
      ? [row, ...flattenLevel(node.children, depth + 1, options, options.hasMoreChildren?.(node) ?? false)]
      : [row];
  });
};

/**
 * A tree as the one flat list a listbox or `tree` widget navigates by index, every row carrying
 * where it sits so it can be announced without the shape it came from. Paging is per level: roots
 * via `hasMoreRootNodes`, a node's children via `hasMoreChildren`.
 */
export const flattenTree = <TValue, TRow>(
  roots: TreeLevel<TValue>,
  options: FlattenTreeOptions<TValue, TRow>,
): TRow[] => flattenLevel(roots, 0, options, options.hasMoreRootNodes ?? false);
